import React from 'react';

/**
 * THE REAL BRAND FILES.
 *
 * Both of these are the exact assets the site header uses (see Layout.tsx) —
 * not a lookalike and not typeset. An earlier version of this file set the
 * wordmark in Baloo 2 beside the mark, which is close enough to pass on a
 * page and wrong the moment anyone puts it next to the real one.
 *
 * Vector.png is the mark; giga4x.png is the wordmark. On dark footage both are
 * flipped with the same brightness/invert filter the header uses on its dark
 * plates, so the reversed pair is derived from the originals rather than being
 * a second set of files that can drift.
 */
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png';
const GIGA_WORDMARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png';

/** The header's own treatment for placing the brand on something dark. */
const REVERSE = 'brightness(0) invert(1)';

/**
 * BRAND MARKS FOR VIDEO.
 *
 * A creator needs a mark far more often than a screen: a corner bug on the
 * clip, a logo beside a tool's logo when saying "Giga reads your Salesforce",
 * a thumbnail. The screen library had lockups (mark + word in a capsule) but
 * never the bare marks, so anyone wanting just the Giga glyph was cropping it
 * out of a screenshot.
 *
 * These export on a genuinely transparent artboard at 4× — a mark is placed
 * small and scaled by whoever uses it, so it needs the headroom the wide
 * frames do not.
 */

export const GIGA_ORANGE = '#EA580C';

/** Giga's own marks, in the forms a video actually asks for. */
export type BrandAsset = {
  id: string;
  name: string;
  note: string;
  /**
   * Rendered size on the artboard, before the 4× export. EVERY asset is the
   * same square: a set of marks at mixed aspect ratios cannot be swapped for
   * one another, and the two that are most likely to be used — an avatar and a
   * title-card stamp — are square by definition.
   */
  boxW: number;
  boxH: number;
  /**
   * The asset carries its own opaque ground, so the preview shows it on a
   * plain well rather than on the transparency checkerboard (which otherwise
   * appears as a border around a tile that has no transparency to show).
   */
  filled?: boolean;
  render: () => React.ReactNode;
};

/** Uniform artboard. 4× on export puts every mark out at 512px. */
const BOX = 128;

/**
 * The mark recoloured to an arbitrary brand colour.
 *
 * A CSS `filter` chain can only approximate a target hue from a black PNG, and
 * "approximately #EA580C" is not a brand colour. Masking paints the real
 * accent through the glyph's own alpha instead, so the orange is exact.
 */
function MarkGlyph({
  color,
  size
}: {
  color: string;
  size: number;
}) {
  return <span aria-hidden="true" className="block" style={{
    width: size,
    height: size,
    backgroundColor: color,
    maskImage: `url(${GIGA_MARK})`,
    WebkitMaskImage: `url(${GIGA_MARK})`,
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center'
  }} data-id="element-3231" data-mp="1vxi54nuodmz4" />;
}

/** A filled brand tile — the shared shape for every non-transparent mark. */
function Tile({
  ground,
  children
}: {
  ground: string;
  children: React.ReactNode;
}) {
  return <div className="flex size-full items-center justify-center rounded-[26px]" style={{
    backgroundColor: ground
  }} data-id="element-3232" data-mp="2ytgm87tumcc">
      {children}
    </div>;
}

/**
 * Mark above wordmark. Stacked rather than side by side because the artboard
 * is square — a horizontal lockup in a square frame is mostly empty space, and
 * the wordmark ends up too small to read once the whole thing is placed.
 */
function Lockup({
  markColor,
  wordReversed
}: {
  markColor?: string;
  wordReversed?: boolean;
}) {
  return <span className="inline-flex flex-col items-center gap-2.5" data-id="element-3233" data-mp="29sx6ge2arocm">
      {markColor ? <MarkGlyph color={markColor} size={46} data-id="element-3234" data-mp="2b7gepvmzj9e4" /> : <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[46px] object-contain" data-id="element-3235" data-mp="2g7cipuvon0to" />}
      <img src={GIGA_WORDMARK} alt="Giga" className="h-[26px] object-contain" style={{
      filter: wordReversed ? REVERSE : undefined
    }} data-id="element-3236" data-mp="2g95svk5i836f" />
    </span>;
}
const INK = '#2B211A';

/*
 * FIVE MARKS, ONE SQUARE. Two transparent (drop onto anything), three filled
 * tiles (ink, ink with the accent mark, orange). A CORNER BUG — the horizontal
 * ink pill — used to close this list; it is gone because it was the only
 * non-square asset left, and the ink tile does the same job in a shape that
 * also works as an avatar.
 */
export const BRAND_ASSETS: BrandAsset[] = [{
  id: 'brand-mark',
  name: 'Giga mark',
  note: 'Glyph only, transparent',
  boxW: BOX,
  boxH: BOX,
  render: () => <img src={GIGA_MARK} alt="Giga" className="size-[86px] object-contain" data-id="element-3237" data-mp="19p68l4zkh165" />
}, {
  id: 'brand-wordmark',
  name: 'Lockup',
  note: 'Mark and wordmark, transparent',
  boxW: BOX,
  boxH: BOX,
  render: () => <Lockup data-id="element-3238" data-mp="3ryitfnrpn89x" />
}, {
  id: 'brand-mark-white',
  name: 'Mark on ink',
  note: 'The reversed glyph, on its own tile',
  boxW: BOX,
  boxH: BOX,
  filled: true,
  render: () => <Tile ground={INK} data-id="element-3239" data-mp="3iynj1l5zbhay">
      <MarkGlyph color="#FFFFFF" size={72} data-id="element-3240" data-mp="3j21hwjffp8dn" />
    </Tile>
}, {
  id: 'brand-wordmark-white',
  name: 'Lockup on ink',
  note: 'Accent mark, white wordmark — the default for dark footage',
  boxW: BOX,
  boxH: BOX,
  filled: true,
  // The mark in the accent rather than white: on ink the two-colour lockup is
  // the livelier of the pair, and it is the only place the accent appears in
  // the brand set apart from the orange tile.
  render: () => <Tile ground={INK} data-id="element-3241" data-mp="2vaw1a21o6c2a">
      <Lockup markColor={GIGA_ORANGE} wordReversed data-id="element-3242" data-mp="2vbnn4l5gsvfd" />
    </Tile>
}, {
  id: 'brand-mark-orange',
  name: 'Mark on orange',
  note: 'The accent as the ground — title card or avatar',
  boxW: BOX,
  boxH: BOX,
  filled: true,
  // White rather than ink: #2B211A on #EA580C is too close in value to hold at
  // small sizes, and the accent is only ever paired with white on the site.
  render: () => <Tile ground={GIGA_ORANGE} data-id="element-3243" data-mp="2goox8um9a92r">
      <MarkGlyph color="#FFFFFF" size={76} data-id="element-3244" data-mp="2glknlhq0ul4p" />
    </Tile>
}];

/**
 * The closer's ground — the warm near-black the rendered MP4s resolve on.
 * Sampled from the closer's final frame so the still card and the video are
 * literally the same colour; anything close-but-different reads as a mistake
 * when they are cut back to back.
 */
export const CLOSER_GROUND = '#3A322C';

/**
 * THE END CARD — the closer's last frame, frozen.
 *
 * Deliberately identical to where the rendered MP4 lands: the address in the
 * rounded wordmark face, white, centred, on the warm near-black ground, and
 * nothing else. An earlier version stacked a mark, the name and a strapline
 * with the address in a pill — a nicer composition, and wrong, because a clip
 * that ends on the video and one that ends on this card would have looked like
 * they came from two different companies.
 *
 * It carries type — the exception to the rule that these plates stay
 * captionless — because an end card IS the caption, and the words never
 * change.
 */
export function EndCard(_props: {
  ratio?: '9:16' | '16:9';
}) {
  return <div className="flex h-full w-full items-center justify-center" style={{
    backgroundColor: CLOSER_GROUND
  }} data-id="element-3245" data-mp="1gepewsfbm3yl">
      {/*
       * ONE SIZE FOR BOTH SHAPES. It used to be 46 on the vertical plate and
       * 64 on the landscape one, which made the landscape address a third
       * larger — the two plates share a 540px short edge, so sizing against
       * the long edge silently changed the lockup between them. It is the same
       * closer twice; only the ground around it changes.
       *
       * `font-title` is the site display face (Inter Display, pinned to 560 in
       * index.css), not the rounded wordmark face. The wordmark is the word
       * "Giga"; an ADDRESS set in the logo face is a logo that does not exist,
       * and it read as a different typeface from everything else in the pack.
       *
       * leading-[1.1] rather than leading-none: with line-height 1 the "g"
       * descender falls outside the text box, so flex centring balanced the
       * box and left the word itself sitting visibly high.
       */}
      <p className="font-title text-[46px] leading-[1.1] tracking-[-0.02em] text-white" data-id="element-3246" data-mp="g7k6hntrw19l">
        getgiga.com
      </p>
    </div>;
}

/*
 * A LOWER THIRD used to live here — a name strip for whoever is on camera.
 * Removed rather than kept: this page is for affiliates and creators, and a
 * strip carrying a fixed Giga employee's name and title is not an asset any of
 * them can use. If an internal, editable version is ever wanted, it belongs on
 * an internal tool, not in a public download page.
 */